
import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Combobox } from "@/components/ui/combobox";
import { Switch } from "@/components/ui/switch";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { FreelancerAutocomplete } from "@/components/freelancers/FreelancerAutocomplete";
import { formatLocalDate, createLocalDate } from "@/utils/dateUtils";
import { AREAS_DO_PROJETO } from "@/components/controle-projetos/areas";
import type { ProjetoModalidade } from "@/types/controle-projeto";
import { MOEDAS, parseMoeda, simboloMoeda } from "@/utils/money";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const AREAS_ATUACAO = AREAS_DO_PROJETO.map((area) => ({ value: area, label: area }));

interface ControleProjetoFormProps {
  projeto?: any;
  onSave: (projeto: any) => void;
  onCancel: () => void;
  mode?: 'create' | 'edit' | 'promote';
}

export function ControleProjetoForm({ projeto, onSave, onCancel, mode = projeto ? 'edit' : 'create' }: ControleProjetoFormProps) {
  const [formData, setFormData] = useState({
    agencia_id: projeto?.agencia_id || "",
    freelancer_id: projeto?.freelancer_id || "",
    area_atuacao: projeto?.area_atuacao || "",
    nucleo: projeto?.nucleo || "",
    valor_projeto: projeto?.valor_projeto || "",
    moeda: projeto?.moeda ?? null,
    data_inicio: projeto?.data_inicio ? createLocalDate(projeto.data_inicio) : undefined,
    data_conclusao: projeto?.data_conclusao ? createLocalDate(projeto.data_conclusao) : undefined,
    vaga_id: projeto?.vaga_id || "",
    modalidade: (projeto?.modalidade === "fixa" ? "fixa" : "frila") as ProjetoModalidade,
  });

  const [agencias, setAgencias] = useState<any[]>([]);
  const [vagas, setVagas] = useState<{ id: string; agencia: string; areas: string | null; cliente: string | null; moeda: string | null }[]>([]);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    fetchAgencias();
    fetchVagas();
  }, []);

  const fetchAgencias = async () => {
    const { data } = await supabase
      .from("agencias")
      .select("id, nome, moeda_padrao")
      .order("nome");
    setAgencias(data || []);
  };

  const fetchVagas = async () => {
    const { data } = await supabase
      .from("vagas")
      .select("id, agencia, areas, cliente, moeda")
      .order("created_at", { ascending: false })
      .limit(500);
    setVagas(data || []);
  };

  // Sem escolha no projeto, vale a da vaga, senão a padrão da agência (o banco preenche ao criar).
  const moedaDoProjeto = parseMoeda(
    formData.moeda ??
      vagas.find((vaga) => vaga.id === formData.vaga_id)?.moeda ??
      agencias.find((agencia) => agencia.id === formData.agencia_id)?.moeda_padrao,
  );

  const vagaOptions = useMemo(() => {
    return vagas.map(v => ({
      value: v.id,
      label: `#${v.id.substring(0, 6)} • ${v.agencia || v.cliente || "—"}${v.areas ? ` • ${v.areas}` : ""}`,
    }));
  }, [vagas]);

  // Preparar opções para o combobox de agências
  const agenciaOptions = useMemo(() => {
    return agencias.map(agencia => ({
      value: agencia.id,
      label: agencia.nome
    }));
  }, [agencias]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    // Promoção exige data_inicio (CHECK constraint do banco também valida)
    if (mode === 'promote' && !formData.data_inicio) {
      setValidationError("Defina a data de início para ativar o projeto.");
      return;
    }
    if (mode !== 'edit' && formData.modalidade === "frila" && !formData.data_conclusao) {
      setValidationError("Defina a data de conclusão ou marque vaga fixa.");
      return;
    }

    setLoading(true);

    try {
      const isPromote = mode === 'promote';
      const projetoData: Record<string, unknown> = {
        agencia_id: formData.agencia_id,
        freelancer_id: formData.freelancer_id,
        area_atuacao: formData.area_atuacao,
        nucleo: formData.nucleo || null,
        valor_projeto: formData.valor_projeto ? parseFloat(formData.valor_projeto) : null,
        ...(formData.moeda ? { moeda: formData.moeda } : {}),
        data_inicio: formatLocalDate(formData.data_inicio),
        data_conclusao: formData.modalidade === "fixa" ? null : formatLocalDate(formData.data_conclusao),
        vaga_id: formData.vaga_id || null,
        modalidade: formData.modalidade,
      };

      if (isPromote) {
        projetoData.status = 'ativo';
      } else if (mode === 'create') {
        projetoData.status = 'ativo';
      }
      // mode === 'edit': preserva status atual (não envia o campo)

      onSave(projetoData);
    } catch (error) {
      console.error("Erro ao salvar projeto:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {mode === 'promote' && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          Você está completando um projeto em <strong>rascunho</strong>. Ao salvar,
          ele passa a aparecer na listagem normal de projetos ativos.
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="vaga">Vaga vinculada</Label>
        <Combobox
          options={vagaOptions}
          value={formData.vaga_id}
          onValueChange={(value) => setFormData({ ...formData, vaga_id: value })}
          placeholder="Selecione a vaga (opcional)"
          searchPlaceholder="Buscar vaga..."
          emptyText="Nenhuma vaga encontrada."
          allowCustomValue={false}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="agencia">Agência</Label>
        <Combobox
          options={agenciaOptions}
          value={formData.agencia_id}
          onValueChange={(value) => setFormData({ ...formData, agencia_id: value })}
          placeholder="Selecione ou digite uma agência"
          searchPlaceholder="Buscar agência..."
          emptyText="Nenhuma agência encontrada."
          allowCustomValue={true}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="freelancer">Freelancer</Label>
        <FreelancerAutocomplete
          value={formData.freelancer_id}
          onValueChange={(id) => setFormData({ ...formData, freelancer_id: id })}
          selectedLabel={projeto?.freelancer?.nome}
          placeholder="Selecione um freelancer"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="area_atuacao">Área de Atuação</Label>
        <Combobox
          options={AREAS_ATUACAO}
          value={formData.area_atuacao}
          onValueChange={(value) => setFormData({ ...formData, area_atuacao: value })}
          placeholder="Selecione ou digite uma área de atuação"
          searchPlaceholder="Buscar área de atuação..."
          emptyText="Nenhuma área encontrada."
          allowCustomValue={true}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="nucleo">Núcleo</Label>
        <Input
          id="nucleo"
          type="text"
          value={formData.nucleo}
          onChange={(e) => setFormData({ ...formData, nucleo: e.target.value })}
          placeholder="Ex: Criação, Mídia, Digital..."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="moeda">Moeda</Label>
        <Select value={moedaDoProjeto} onValueChange={(moeda) => setFormData({ ...formData, moeda })}>
          <SelectTrigger id="moeda">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MOEDAS.map((opcao) => (
              <SelectItem key={opcao.value} value={opcao.value}>
                {opcao.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="valor_projeto">Valor do Projeto ({simboloMoeda(moedaDoProjeto)})</Label>
        <Input
          id="valor_projeto"
          type="number"
          step="0.01"
          value={formData.valor_projeto}
          onChange={(e) => setFormData({ ...formData, valor_projeto: e.target.value })}
          placeholder="Ex: 5000.00"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Data de Início</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !formData.data_inicio && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.data_inicio ? (
                  format(formData.data_inicio, "dd/MM/yyyy", { locale: ptBR })
                ) : (
                  "Selecione a data"
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={formData.data_inicio}
                onSelect={(date) => setFormData({ ...formData, data_inicio: date })}
                autoFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label>Data de Conclusão</Label>
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={formData.modalidade === "fixa"}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, modalidade: checked ? "fixa" : "frila", data_conclusao: checked ? undefined : formData.data_conclusao })
              }
            />
            Vaga fixa (sem data final)
          </label>
          {formData.modalidade === "frila" && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.data_conclusao && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.data_conclusao ? (
                    format(formData.data_conclusao, "dd/MM/yyyy", { locale: ptBR })
                  ) : (
                    "Selecione a data"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={formData.data_conclusao}
                  onSelect={(date) => setFormData({ ...formData, data_conclusao: date })}
                  autoFocus
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {validationError && (
        <p className="text-sm text-red-600">{validationError}</p>
      )}

      <div className="flex gap-2 pt-4">
        <Button type="submit" disabled={loading} className="flex-1">
          {loading
            ? "Salvando..."
            : mode === 'promote'
            ? "Ativar Projeto"
            : projeto
            ? "Atualizar Projeto"
            : "Criar Projeto"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancelar
        </Button>
      </div>
    </form>
  );
}
